import {ErrorMessage, Loader, SharePreviewCard} from '@communityshare/ui-shared';
import {useConnect} from '@lore/query-connect';
import {PayloadStates} from '@lore/utils';
import {useConfig} from '@lore/config';
import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import useProjectImage from '@/features/share-project/hooks/useProjectImage';
import usePreviewImages from '@/features/share-common/hooks/usePreviewImages';
import getProjectShareUrl from '@/features/share-project/utils/getProjectShareUrl';

// Window name for the preview tab: repeat clicks reuse the tab instead of
// opening new ones.
const PREVIEW_WINDOW_NAME = 'cs-share-project-preview';

ProjectLinkPreview.propTypes = {
  project: PropTypes.shape({
    id: PropTypes.number.isRequired,
    state: PropTypes.string,
    data: PropTypes.shape({
      activityId: PropTypes.number,
      siteId: PropTypes.number,
      title: PropTypes.string
    })
  }).isRequired
};

export default function ProjectLinkPreview(props) {
  const { project } = props;
  const { t } = useTranslation();
  const config = useConfig();
  // Default to tag 1 so byId never throws for projects without an activity
  const activity = useConnect('tag.byId', {
    id: project?.data?.activityId ?? 1
  });
  const siteId = project?.data?.siteId ?? null;
  // Pass 0 when siteId is absent, so the blueprint doesn't throw 'Missing id';
  // a 0-id query returns NOT_FOUND harmlessly. Location is omitted in the
  // render when siteId is null (see below).
  const site = useConnect('site.byId', {
    id: siteId ?? 0
  });
  // Local selection; null defers to the saved image (or the default)
  const [selectedImageId, setSelectedImageId] = useState(null);
  const projectImage = useProjectImage(project.id);
  const { loading: imagesLoading, images } = usePreviewImages(project.data.activityId);
  const defaultSavedRef = useRef(false);

  const shareUrl = getProjectShareUrl(config, project);
  const shareHostname = new URL(shareUrl).hostname;

  // A project with no saved image gets the default persisted once,
  // so the share page renders the same photo the preview shows.
  // Waits for the activity's image set so the right default is saved.
  useEffect(() => {
    if (projectImage.isSuccess && !projectImage.image && !imagesLoading && !defaultSavedRef.current) {
      defaultSavedRef.current = true;
      const image = images[0];
      projectImage.upsert({ imageUrl: image.src, width: image.width, height: image.height });
    }
  }, [projectImage.isSuccess, projectImage.image, imagesLoading, images]);

  // loading project, its saved share image, or the activity's image set
  if ([PayloadStates.FETCHING, PayloadStates.INITIAL_STATE].includes(project.state) || projectImage.isLoading || imagesLoading) {
    return (
      <div className="flex flex-col justify-center h-[80vh]">
        <Loader/>
      </div>
    );
  }

  // unexpected error state
  if (![PayloadStates.RESOLVED, PayloadStates.UPDATING].includes(project.state) || !project.data) {
    return (
      <div className="flex flex-col items-center">
        <ErrorMessage v2={true}>
          {t('project_share.data_error')}
        </ErrorMessage>
      </div>
    );
  }

  // Local selection wins (optimistic), then the saved image;
  // null renders the card's placeholder until an image exists
  const savedImage = images.find(image => image.src === projectImage.image?.imageUrl);
  const shareImage = images.find(image => image.id === selectedImageId) ?? savedImage ?? null;

  // The preview swaps immediately while the save runs;
  // a failure surfaces through projectImage.upsertError below.
  function onShareImageChange(imageId) {
    setSelectedImageId(imageId);
    const image = images.find(image => image.id === imageId);
    projectImage.upsert({
      imageUrl: image.src,
      width: image.width,
      height: image.height
    });
  }

  function onPreview() {
    window.open(shareUrl, `${PREVIEW_WINDOW_NAME}-${project.id}`);
  }

  const location = siteId && site.state !== PayloadStates.FETCHING ?
    [site?.data?.city, site?.data?.state].filter(Boolean).join(', ') :
    '';
  const activityName = activity?.data?.name ?? '...';

  // project is loaded or updating (probably saving the title)
  return (
    <SharePreviewCard
      title={t('project_share.link_preview')}
      imageSrc={shareImage?.src ?? null}
      imageKey={shareImage?.id}
      imageAlt={t('project_share.preview_image_alt')}
      urlText={`${shareHostname}/share/project/${project.id}`}
      eyebrowText={
        <>
          {activity.state === PayloadStates.FETCHING && <Loader/>}
          {location ? `${activityName} | ${location}` : activityName}
        </>
      }
      primaryText={t('project_share.cta_line')}
      secondaryText={<>&ldquo;{project.data.title}&rdquo;</>}
      previewLabel={t('project_share.preview_page')}
      onPreview={onPreview}
      images={images}
      selectedImageId={shareImage?.id ?? null}
      onImageChange={onShareImageChange}
      swapPhotoLabel={t('project_share.swap_photo')}
      getImageLabel={(image, index) => t('project_share.photo_option', { number: index + 1 })}
      errorMessage={projectImage.upsertError ? t('project_share.image_error') : null}
    />
  );
}
